import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customDashboardApi, formsApi } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  LayoutDashboard, Plus, Trash2, Edit, Star, StarOff, Loader2,
  BarChart3, FileText, Eye, Share2, Users
} from 'lucide-react';

const PRESET_COLORS = ['#0E7490', '#166534', '#7C3AED', '#DC2626', '#CA8A04', '#1A56DB', '#92400E', '#0891B2', '#BE185D', '#4338CA', '#059669', '#D97706'];

export default function CustomDashboardsPage() {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#0E7490');
  const [selectedForms, setSelectedForms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showShare, setShowShare] = useState(null);
  const [shareUsers, setShareUsers] = useState([]);
  const [selectedShareUsers, setSelectedShareUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [dashRes, formsRes] = await Promise.all([
        customDashboardApi.list(),
        formsApi.list()
      ]);
      setDashboards(dashRes.data.dashboards || []);
      setForms(formsRes.data.forms || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditId(null); setName(''); setDescription(''); setColor('#0E7490'); setSelectedForms([]);
    setShowCreate(true);
  };

  const openEdit = (d) => {
    setEditId(d.id); setName(d.name); setDescription(d.description || '');
    setColor(d.color || '#0E7490'); setSelectedForms(d.form_ids || []);
    setShowCreate(true);
  };

  const toggleForm = (fid) => {
    setSelectedForms(prev => prev.includes(fid) ? prev.filter(x => x !== fid) : [...prev, fid]);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Dashboard name required'); return; }
    if (selectedForms.length === 0) { toast.error('Select at least one form'); return; }
    setSaving(true);
    try {
      const payload = { name, description, color, form_ids: selectedForms };
      if (editId) {
        await customDashboardApi.update(editId, payload);
        toast.success('Dashboard updated');
      } else {
        await customDashboardApi.create(payload);
        toast.success('Dashboard created');
      }
      setShowCreate(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await customDashboardApi.delete(id);
      toast.success('Dashboard deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleSetHomepage = async (id, isCurrentlyHomepage) => {
    try {
      if (isCurrentlyHomepage) {
        await customDashboardApi.unsetHomepage(id);
        toast.success('Removed from homepage');
      } else {
        await customDashboardApi.setHomepage(id);
        toast.success('Set as homepage tab');
      }
      load();
    } catch { toast.error('Failed to update'); }
  };

  const openShareDialog = async (d) => {
    setShowShare(d);
    setSelectedShareUsers(d.shared_with || []);
    try {
      const res = await api.get('/users');
      setShareUsers(res.data.users || res.data || []);
    } catch { setShareUsers([]); }
  };

  const handleShare = async () => {
    try {
      await customDashboardApi.share(showShare.id, selectedShareUsers);
      toast.success('Dashboard shared');
      setShowShare(null);
      load();
    } catch { toast.error('Failed to share'); }
  };

  return (
    <div className="space-y-6" data-testid="custom-dashboards-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>My Dashboards</h1>
          <p className="text-[#64748B] mt-1">Create custom dashboards from your forms</p>
        </div>
        <Button className="careiq-btn-primary" onClick={openCreate} data-testid="create-dashboard-btn">
          <Plus className="w-4 h-4 mr-2" />New Dashboard
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>
      ) : dashboards.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dashboards.map((d) => (
            <Card key={d.id} className="careiq-card overflow-hidden hover:shadow-md transition-shadow" data-testid={`dashboard-card-${d.id}`}>
              <div className="h-2" style={{ backgroundColor: d.color || '#0E7490' }} />
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#0A2342] truncate">{d.name}</h3>
                    {d.description && <p className="text-xs text-[#64748B] mt-0.5 line-clamp-2">{d.description}</p>}
                  </div>
                  {d.is_homepage && (
                    <span className="text-[10px] px-2 py-0.5 bg-[#CA8A04]/15 text-[#CA8A04] rounded-full font-medium ml-2">Homepage</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  <span className="text-[10px] px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded-full">{d.forms?.length || 0} forms</span>
                  {d.forms?.slice(0, 3).map((f, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: f.category_color || '#64748B' }}>
                      {f.name?.length > 15 ? f.name.slice(0, 15) + '...' : f.name}
                    </span>
                  ))}
                </div>
                <div className="flex gap-1 pt-1">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/dashboards/${d.id}`)} data-testid={`view-dashboard-${d.id}`}>
                    <Eye className="w-3 h-3 mr-1" />View
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-dashboard-${d.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleSetHomepage(d.id, d.is_homepage)} data-testid={`homepage-toggle-${d.id}`}>
                    {d.is_homepage ? <Star className="w-3.5 h-3.5 text-[#CA8A04] fill-[#CA8A04]" /> : <StarOff className="w-3.5 h-3.5 text-[#94A3B8]" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openShareDialog(d)} data-testid={`share-dashboard-${d.id}`} title="Share">
                    <Share2 className="w-3.5 h-3.5 text-[#0E7490]" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id)}><Trash2 className="w-3.5 h-3.5 text-[#991B1B]" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="careiq-card"><CardContent className="py-16 text-center text-[#64748B]">
          <LayoutDashboard className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
          <p className="font-medium">No custom dashboards yet</p>
          <p className="text-sm mt-1">Create one to track your form submissions at a glance</p>
        </CardContent></Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg" data-testid="dashboard-form-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Edit Dashboard' : 'Create Dashboard'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Dashboard Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Intake Tracking" data-testid="dashboard-name-input" /></div>
            <div><Label>Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description" /></div>
            <div>
              <Label>Dashboard Color</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PRESET_COLORS.map((c) => (
                  <button key={c} onClick={() => setColor(c)} className={`w-8 h-8 rounded-lg border-2 transition-all ${color === c ? 'border-[#0A2342] scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} data-testid={`color-${c}`} />
                ))}
              </div>
            </div>
            <div>
              <Label>Select Forms *</Label>
              <div className="max-h-48 overflow-y-auto mt-1 space-y-1 border border-[#E2E8F0] rounded-lg p-2">
                {forms.map((f) => (
                  <label key={f.id} className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${selectedForms.includes(f.id) ? 'bg-[#0E7490]/10' : 'hover:bg-[#F8FAFC]'}`}>
                    <input type="checkbox" checked={selectedForms.includes(f.id)} onChange={() => toggleForm(f.id)} className="rounded" />
                    <FileText className="w-3.5 h-3.5 text-[#0E7490]" />
                    <span className="text-sm text-[#0A2342] flex-1">{f.name}</span>
                    {f.category && <span className="text-[9px] px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: getCategoryColor(f.category) }}>{f.category}</span>}
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-1">{selectedForms.length} form(s) selected</p>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-dashboard-btn">
                {saving ? 'Saving...' : editId ? 'Update' : 'Create Dashboard'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={!!showShare} onOpenChange={() => setShowShare(null)}>
        <DialogContent className="sm:max-w-md" data-testid="share-dashboard-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-[#0E7490]" />Share Dashboard</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-[#64748B]">Share "{showShare?.name}" with other users. They can view it and set it as their homepage.</p>
            <div className="space-y-1 max-h-[250px] overflow-y-auto border rounded-lg p-2">
              {shareUsers.map(u => (
                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 hover:bg-[#F8FAFC] rounded">
                  <input type="checkbox" checked={selectedShareUsers.includes(u.id)} onChange={() => {
                    setSelectedShareUsers(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id]);
                  }} className="rounded" />
                  <span className="flex-1">{u.name}</span>
                  <span className="text-[10px] text-[#64748B] px-1.5 py-0.5 bg-[#F1F5F9] rounded">{u.role}</span>
                </label>
              ))}
              {shareUsers.length === 0 && <p className="text-xs text-[#94A3B8] text-center py-4">No users found</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowShare(null)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleShare} data-testid="confirm-share-btn"><Share2 className="w-4 h-4 mr-2" />Share</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function getCategoryColor(cat) {
  const map = {
    intake: '#0E7490', assessment: '#166534', consent: '#92400E',
    survey: '#7C3AED', clinical: '#DC2626', administrative: '#1A56DB',
    billing: '#CA8A04', referral: '#0891B2'
  };
  return map[cat] || '#64748B';
}
